import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../AuthContext";
import logo from "../images/dc_logo.png";
import "./MentorDetails.css";

// const BASE_URL = "http://localhost:8000";
const BASE_URL = "https://idealpursuits.co.in/api";

function MentorDetails() {
    const { mentorId } = useParams();
    const [isChecked, setIsChecked] = useState(false);
    const [mentor, setMentor] = useState(null);
    const [scrollY, setScrollY] = useState(0);
    const [meetingLink, setMeetingLink] = useState(null);
    const [selectedDate, setSelectedDate] = useState("");
    const [selectedTime, setSelectedTime] = useState("");
    const [availableDates, setAvailableDates] = useState([]);
    const [availableTimes, setAvailableTimes] = useState([]);
    const [popupType, setPopupType] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const { isLogged, userData } = useAuth();
    const [dateTimeMap, setDateTimeMap] = useState({});
    const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchMentorDetails = async () => {
            try {
                // Fetch mentor details
                const mentorResponse = await axios.get(`${BASE_URL}/mentor/getMentorDetails.php?mentorId=${mentorId}`);
                const mentorData = mentorResponse.data;
                setMentor(mentorData);

                // Fetch mentor dates
                const datesResponse = await axios.get(`${BASE_URL}/mentor/getMentorDatesForUser.php?mentorId=${mentorId}`);
                const datesData = datesResponse.data;

                // Get the current date and time
                const currentTime = new Date().getTime();

                // Process dates and times
                const dateTimeMap = {}; // Object to store times for each date
                const availableDates = [];

                datesData.forEach((entry) => {
                    const { id, date, times: time } = entry;
                    const entryDateTime = new Date(`${date}T${time}`).getTime();

                    // Filter for upcoming dates and times at least 12 hours ahead
                    if (entryDateTime > currentTime + 12 * 60 * 60 * 1000) {
                        if (!dateTimeMap[date]) {
                            dateTimeMap[date] = [];
                            availableDates.push(date); // Add date only once
                        }
                        dateTimeMap[date].push({ id, time }); // Retain id along with time
                    }
                });

                // Sort dates in ascending order
                availableDates.sort((a, b) => new Date(a) - new Date(b));

                // Update state
                setAvailableDates(availableDates);
                setDateTimeMap(dateTimeMap); // Store date-time mapping with IDs

                // Set default selected date and time
                if (availableDates.length > 0) {
                    setSelectedDate(availableDates[0]);
                    const sortedTimes = dateTimeMap[availableDates[0]].sort((a, b) => a.time.localeCompare(b.time));
                    setAvailableTimes(sortedTimes);
                    setSelectedTime(sortedTimes[0]);
                }
            } catch (error) {
                console.error("Error fetching mentor details or dates:", error);
            }
        };

        fetchMentorDetails();
    }, [mentorId]);

    const convertTo12HrFormat = (time) => {
        const [hour, minute] = time.split(":");
        let newHour = hour % 12;
        newHour = newHour ? newHour : 12;
        const ampm = hour >= 12 ? "PM" : "AM";
        return `${newHour}:${minute} ${ampm}`;
    };

    const initializeRazorpay = () => {
        return new Promise((resolve) => {
            const script = document.createElement("script");
            script.src = "https://checkout.razorpay.com/v1/checkout.js";
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
        });
    };

    const handlePayment = async () => {
        try {
            setIsProcessing(true);
            const res = await initializeRazorpay();

            if (!res) {
                alert("Razorpay SDK failed to load");
                setIsProcessing(false);
                return;
            }

            // Create order
            const orderResponse = await axios.post(`${BASE_URL}/create-meeting-order.php`, {
                amount: mentor.meetingCharge * 100, // Convert to paise
                currency: "INR",
                tutorId: mentor.mentorId,
                userId: userData.userId,
                meetingDate: selectedDate,
                meetingTime: convertTo12HrFormat(selectedTime.time)
            });

            const options = {
                key: "rzp_live_126ap0g6lfYF7b",
                amount: mentor.meetingCharge * 100,
                currency: "INR",
                name: "The Ideal Pursuits",
                description: `Session with ${mentor.name}`,
                order_id: orderResponse.data.orderId,
                handler: async (response) => {
                    try {
                        // Verify payment
                        const verifyResponse = await axios.post(`${BASE_URL}/verify-meeting-payment.php`, {
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_signature: response.razorpay_signature
                        });

                        if (verifyResponse.data.status === "success") {
                            await handlePaymentSuccess();
                        } else {
                            alert("Payment verification failed");
                        }
                    } catch (error) {
                        console.error("Payment verification error:", error);
                        alert("Payment verification failed");
                    }
                },
                prefill: {
                    name: userData.name,
                    email: userData.email
                },
                theme: {
                    color: "#3399cc"
                },
                modal: {
                    ondismiss: () => {
                        setIsProcessing(false);
                    }
                }
            };

            const paymentObject = new window.Razorpay(options);
            paymentObject.open();
        } catch (error) {
            console.error("Payment initiation error:", error);
            alert("Failed to initiate payment");
            setIsProcessing(false);
        }
    };

    const handleContinue = () => {
        if (selectedDate && selectedTime) {
            if (!isLogged) {
                navigate("/login", { state: { returnUrl: `/career-coach-profile/${mentorId}` } });
            } else {
                setPopupType("terms");
            }
        } else {
            alert("Please select a date and time.");
        }
    };

    const handleCheckboxChange = (e) => {
        setIsChecked(e.target.checked);
    };

    const handlePaymentSuccess = async () => {

        if (isChecked) {
            try {
                setIsPaymentProcessing(true);
                setPopupType(null);

                const response = await axios.post(`${BASE_URL}/meetings.php`, {
                    userId: userData.userId,
                    tutorId: mentor.mentorId,
                    tutorType: 'mentor',
                    tutorName: mentor.name,
                    userName: userData.name,
                    meetingDate: selectedDate,
                    bookedDateId: selectedTime.id,
                    meetingTime: convertTo12HrFormat(selectedTime.time),
                    meetingCharge: mentor.meetingCharge
                });

                await axios.post(`${BASE_URL}/setMentorDateBooked.php`, {
                    id: selectedTime.id
                });

                await axios.post(`${BASE_URL}/setMeetingAttended.php`, {
                    userId: userData.userId
                });

                if (response.data.status === "success") {
                    setMeetingLink(response.data.meetingLink);
                    setIsPaymentProcessing(false);
                    setPopupType("success");
                } else {
                    setIsPaymentProcessing(false);
                    alert("Failed to schedule the meeting: " + response.data.message);
                }
            } catch (error) {
                setIsPaymentProcessing(false);
                console.error("Error scheduling meeting:", error);
                alert("An error occurred. Please try again.");
            }
        }
    };

    if (!mentor) return <div>Loading...</div>;

    const headerStyle = {
        gap: `${Math.min(150, scrollY)}px`,
        marginTop: `${Math.min(15, scrollY / 5)}px`,
        opacity: `${1 - Math.min(scrollY / 300, 0.2)}`,
        width: `${100 - Math.min(scrollY / 5, 20)}%`,
        borderRadius: `${Math.min(15, scrollY / 10)}px`,
    };

    const renderTermsButtons = () => (
        <>
            <button
                className="close-popup-button"
                onClick={() => setPopupType(null)}
                disabled={isProcessing}
            >
                Close
            </button>
            <button
                className={`agree-button ${isProcessing ? 'processing' : ''}`}
                onClick={handlePayment}
                disabled={!isChecked || isProcessing}
            >
                {isProcessing ? 'Processing...' : 'Proceed to Payment'}
            </button>
        </>
    );



    const formatTo12Hr = (time) => {
        const [hour, minute] = time.split(":");
        const formattedHour = hour % 12 || 12;
        const period = hour >= 12 ? "PM" : "AM";
        return `${formattedHour}:${minute} ${period}`;
    };

    const formatToDate = (dateString) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    return (
        <div>
            {isPaymentProcessing && (
                <div className="booking-popup-modal">
                    <div className="loading-popup">
                        <div className="loading-spinner"></div>
                        <p>Processing your booking...</p>
                        <p>Please don't close this window</p>
                    </div>
                </div>
            )}
            
            <header className="header" style={headerStyle}>
                <div className="logo-area">
                    <img className="logo-image" src={logo} alt="logo" />
                    <div className="logo" onClick={() => { navigate('/') }}>The Ideal Pursuits</div>
                </div>
            </header>
            <div className="mentor-details-page">
                <div className="mentor-info-container">
                    {mentor.profileImageName ? (
                        <img
                            src={`${BASE_URL}/images/profile/${mentor.profileImageName}`}
                            alt={mentor.name}
                            className="profile-image"
                        />
                    ) : (
                        <p>No Image</p>
                    )}
                    <h2>{mentor.name}</h2>
                    <p>{mentor.profession}</p>
                    <p>{mentor.category}</p>
                    <p>{mentor.experience}</p>
                    <p>Charges: ₹{mentor.meetingCharge} per session</p>
                </div>
                <div className="booking-details-container">
                    <h3>Book a Session</h3>
                    <p><strong>When do you want to meet?</strong></p>
                    <div className="booking-dates">
                        {availableDates.map((date, index) => (
                            <div
                                key={index}
                                className={`booking-date-item ${selectedDate === date ? "selected" : ""}`}
                                onClick={() => {
                                    setSelectedDate(date);
                                    setAvailableTimes(dateTimeMap[date].sort()); // Update times when date is selected
                                    setSelectedTime(dateTimeMap[date][0]);       // Set the first time as default
                                }}
                            >
                                {formatToDate(date)}
                            </div>
                        ))}
                    </div>

                    <p><strong>Select your preferred time:</strong></p>
                    <div className="booking-times">
                        {availableTimes.map((timeObj, index) => (
                            <div
                                key={timeObj.id}
                                className={`booking-time-item ${selectedTime?.time === timeObj.time ? "selected" : ""}`}
                                onClick={() => setSelectedTime(timeObj)}
                            >
                                {formatTo12Hr(timeObj.time)}
                            </div>
                        ))}
                    </div>

                    <button className="continue-to-payment-button" onClick={handleContinue}>Continue to Payment</button>
                </div>
            </div>

            {popupType === "terms" && (
                <div className="booking-popup-modal">
                    <div className="popup-content">
                        <h2>Terms & Conditions</h2>
                        <p>
                            You agree to be connected with the mentor on the desired date and time.
                            <p><a href="/#/user-mentor-tips" style={{ color: "blue" }} className="terms-link" target="_blank" rel="noopener noreferrer">Click here for more tips!</a></p>
                        </p>

                        <div className="terms-container">
                            <input
                                type="checkbox"
                                id="agreeTerms"
                                className="terms-checkbox"
                                checked={isChecked}
                                onChange={handleCheckboxChange}
                                disabled={isProcessing}
                            />
                            <label htmlFor="agreeTerms" className="terms-label">
                                I agree to the
                                <a href="/#/career-coach-terms-and-conditions" className="terms-link" target="_blank" rel="noopener noreferrer">Terms and Conditions</a>and
                                <a href="/#/privacy-policy" className="terms-link" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                            </label>
                        </div>
                        {renderTermsButtons()}
                    </div>
                </div>
            )}

            {popupType === "success" && (
                <div className="booking-popup-modal">
                    <div className="popup-content">
                        <h2>Payment Successful</h2>
                        <p>
                            Your meeting is scheduled with {mentor.name} on {formatToDate(selectedDate)} at {convertTo12HrFormat(selectedTime.time)}.
                        </p>
                        <p>
                            You can find the meeting link in your <a href='/#/profile'>profile</a> section.
                        </p>
                        <button className="close-popup-button" onClick={() => setPopupType(null)}>
                            Close
                        </button>
                        <button className="profile-button" onClick={() => navigate("/profile")}>
                            Go to Profile
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default MentorDetails;
